import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/time";
import {
  Plus, Copy, ExternalLink, Trash2, RefreshCw, Link2,
  MousePointerClick, UserCheck, Zap, Settings2, CheckCircle2, XCircle,
  CalendarClock, Globe, Eye, Share2, MessageCircle, Mail, Activity, Loader2,
} from "lucide-react";

const API     = process.env.REACT_APP_BACKEND_URL;
const APP_URL = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || "";

const CYCLE_LABEL  = { monthly: "Monthly", yearly: "Yearly" };
const MODE_LABEL   = { preview: "Preview Page", direct: "Direct Reg." };
const EXPIRY_OPTS  = [30, 60, 90];

const fmt = (n, cycle) =>
  `₹${Number(n || 0).toLocaleString("en-IN")}/${cycle === "yearly" ? "yr" : "mo"}`;

export default function SMReferralLinks() {
  const [links,        setLinks]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [partners,     setPartners]     = useState([]);
  const [plans,        setPlans]        = useState([]);
  const [config,       setConfig]       = useState({ default_expiry_days: 30, default_landing_mode: "preview" });
  const [showCreate,   setShowCreate]   = useState(false);
  const [showConfig,   setShowConfig]   = useState(false);
  const [form,         setForm]         = useState({
    partner_id: "", link_type: "prospect",
    plan_id: "", billing_cycle: "monthly", landing_mode: "", expiry_days: "",
  });
  const [cfgForm,      setCfgForm]      = useState({ default_expiry_days: 30, default_landing_mode: "preview" });
  const [saving,       setSaving]       = useState(false);
  const [shareLink,    setShareLink]    = useState(null); // link object whose share dropdown is open
  const [activityLink,    setActivityLink]    = useState(null);
  const [activityData,    setActivityData]    = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [linksR, cfgR] = await Promise.all([
        axios.get(`${API}/api/sm/referral-links`, { withCredentials: true }),
        axios.get(`${API}/api/sm/referral-links/config`, { withCredentials: true }),
      ]);
      setLinks(linksR.data.links || []);
      setConfig(cfgR.data);
      setCfgForm(cfgR.data);
    } catch { toast.error("Failed to load referral links"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API}/api/sm/partners?page=1&limit=100`, { withCredentials: true })
      .then(r => setPartners(r.data.items || r.data.partners || []))
      .catch(() => {});
    axios.get(`${API}/api/plans`, { withCredentials: true })
      .then(r => setPlans(r.data.plans || r.data || []))
      .catch(() => {});
  }, []);

  const shortUrl = (code) => `${window.location.origin}/join/${code}`;

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleCreate = async () => {
    if (!form.partner_id) { toast.error("Select a partner"); return; }
    if (form.link_type === "prospect" && (!form.plan_id || !form.billing_cycle)) {
      toast.error("Select a plan and billing cycle for prospect links"); return;
    }
    setSaving(true);
    try {
      const body = {
        partner_id: form.partner_id,
        link_type:  form.link_type,
        ...(form.link_type === "prospect" && { plan_id: form.plan_id, billing_cycle: form.billing_cycle }),
        ...(form.landing_mode && { landing_mode: form.landing_mode }),
        ...(form.expiry_days  && { expiry_days: Number(form.expiry_days) }),
      };
      await axios.post(`${API}/api/sm/referral-links`, body, { withCredentials: true });
      toast.success("Referral link created");
      setShowCreate(false);
      setForm({ partner_id: "", link_type: "prospect", plan_id: "", billing_cycle: "monthly", landing_mode: "", expiry_days: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this link? Anyone who visits it will see an expired message.")) return;
    try {
      await axios.delete(`${API}/api/sm/referral-links/${id}`, { withCredentials: true });
      toast.success("Link deactivated");
      load();
    } catch { toast.error("Failed to deactivate"); }
  };

  // ── Share helpers ──────────────────────────────────────────────────────────
  const _shareMsg = (l) => {
    const url    = shortUrl(l.short_code);
    const cycle  = l.billing_cycle === "yearly" ? "Annual" : "Monthly";
    return (
      `Hi ${l.partner_name},\n\n` +
      `Your referral link for the *${l.plan_name} (${cycle})* plan is ready:\n\n` +
      `${url}\n\n` +
      `Share this with your prospects. All clicks, registrations and activations are tracked automatically in your partner dashboard.\n\n` +
      `— Team Law OS, Inno Aim Private Limited`
    );
  };

  const shareWhatsApp = (l) => {
    const partner = partners.find(p => p.id === l.partner_id);
    const raw     = (partner?.phone || partner?.mobile || "").replace(/\D/g, "");
    const phone   = raw ? `91${raw.replace(/^91/, "")}` : "";
    const base    = phone ? `https://wa.me/${phone}` : "https://wa.me/";
    window.open(`${base}?text=${encodeURIComponent(_shareMsg(l))}`, "_blank");
    setShareLink(null);
  };

  const shareEmail = (l) => {
    const partner = partners.find(p => p.id === l.partner_id);
    const url     = shortUrl(l.short_code);
    const cycle   = l.billing_cycle === "yearly" ? "Annual" : "Monthly";
    const subject = `Your Law OS Referral Link — ${l.plan_name} (${cycle})`;
    const body    =
      `Hi ${l.partner_name},\n\n` +
      `Your referral link for the ${l.plan_name} (${cycle}) plan is ready:\n\n` +
      `${url}\n\n` +
      `Plan: ${l.plan_name}\n` +
      `Billing: ${cycle}\n` +
      `Expires: ${l.expires_at ? fmtDate(l.expires_at) : "—"}\n\n` +
      `Share this with your prospects. All clicks, registrations and activations are tracked automatically in your partner dashboard.\n\n` +
      `Regards,\nTeam Law OS — Inno Aim Private Limited`;
    const to = partner?.email || "";
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setShareLink(null);
  };

  const loadActivity = async (link) => {
    setActivityLink(link);
    setActivityData([]);
    setActivityLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/sm/referral-links/${link.short_code}/activity`,
        { withCredentials: true }
      );
      setActivityData(r.data.events || []);
    } catch {
      toast.error("Could not load activity");
    } finally {
      setActivityLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/sm/referral-links/config`, cfgForm, { withCredentials: true });
      toast.success("Global config saved");
      setShowConfig(false);
      setConfig(cfgForm);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const totalClicks  = links.reduce((s, l) => s + (l.clicks || 0), 0);
  const totalRegs    = links.reduce((s, l) => s + (l.registrations || 0), 0);
  const totalActs    = links.reduce((s, l) => s + (l.activations || 0), 0);
  const activeCount  = links.filter(l => l.is_active).length;

  const inp = "w-full px-3 py-2 bg-background border border-border rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-primary/50";

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="sm-referral-links">

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Referral Links</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Dynamic short links · Partner + Plan + Billing Cycle
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setShowConfig(true)} data-testid="config-btn"
            className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted transition-all">
            <Settings2 className="w-3.5 h-3.5" />Config
          </button>
          <button onClick={load}
            className="p-2 border border-border rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setShowCreate(true)} data-testid="create-link-btn"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 transition-all shrink-0">
            <Plus className="w-4 h-4" />Generate Link
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Link2,            label: "Active Links",   value: activeCount,  color: "text-primary" },
          { icon: MousePointerClick,label: "Total Clicks",   value: totalClicks,  color: "text-blue-500" },
          { icon: UserCheck,        label: "Registrations",  value: totalRegs,    color: "text-amber-500" },
          { icon: Zap,              label: "Activations",    value: totalActs,    color: "text-emerald-500" },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon className={`w-3.5 h-3.5 ${color}`} />
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
            </div>
            <p className="text-xl font-bold text-foreground">{value}</p>
          </div>
        ))}
      </div>

      {/* Global config strip */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-muted/30 border border-border rounded-xl text-[12px] text-muted-foreground">
        <CalendarClock className="w-3.5 h-3.5 shrink-0" />
        <span>Default expiry: <strong className="text-foreground">{config.default_expiry_days} days</strong></span>
        <span className="text-border">|</span>
        <span>Default landing: <strong className="text-foreground">{MODE_LABEL[config.default_landing_mode]}</strong></span>
        <button onClick={() => setShowConfig(true)} className="text-primary hover:underline ml-auto text-[11px]">Edit</button>
      </div>

      {/* Links table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] min-w-[860px]">
            <thead className="border-b border-border bg-muted/20">
              <tr>
                {["Short Link", "Partner", "Plan", "Cycle", "Landing", "Expires", "Clicks", "Regs.", "Acts.", "Status", "Actions"].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="py-10 text-center text-muted-foreground animate-pulse">Loading…</td></tr>
              )}
              {!loading && links.map(l => {
                const isExpired = l.expires_at && new Date(l.expires_at) < new Date();
                const url = shortUrl(l.short_code);
                return (
                  <tr key={l.id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5">
                        <code className="text-[11px] font-mono text-primary bg-primary/5 px-1.5 py-0.5 rounded">{l.short_code}</code>
                        <button onClick={() => copy(url)} title="Copy link"
                          className="p-1 text-muted-foreground hover:text-foreground transition-colors">
                          <Copy className="w-3 h-3" />
                        </button>
                        <a href={url} target="_blank" rel="noreferrer"
                          className="p-1 text-muted-foreground hover:text-foreground transition-colors">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap font-medium text-foreground">{l.partner_name}</td>
                    <td className="px-3 py-3 whitespace-nowrap text-foreground">
                      {l.link_type === "partner"
                        ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/20">Refer a Partner</span>
                        : l.plan_name
                      }
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {l.link_type === "partner" ? (
                        <span className="text-[9px] text-muted-foreground">—</span>
                      ) : (
                        <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-semibold ${
                          l.billing_cycle === "yearly"
                            ? "bg-violet-500/10 text-violet-600 border border-violet-500/20"
                            : "bg-blue-500/10 text-blue-600 border border-blue-500/20"
                        }`}>
                          {CYCLE_LABEL[l.billing_cycle]}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        {l.landing_mode === "preview" ? <Eye className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                        {MODE_LABEL[l.landing_mode]}
                      </span>
                    </td>
                    <td className={`px-3 py-3 whitespace-nowrap text-[11px] ${isExpired ? "text-red-500" : "text-muted-foreground"}`}>
                      {l.expires_at ? fmtDate(l.expires_at) : "—"}
                    </td>
                    <td className="px-3 py-3 text-center text-foreground font-medium">{l.clicks || 0}</td>
                    <td className="px-3 py-3 text-center text-amber-600 font-medium">{l.registrations || 0}</td>
                    <td className="px-3 py-3 text-center text-emerald-600 font-medium">{l.activations || 0}</td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {l.is_active && !isExpired
                        ? <span className="flex items-center gap-1 text-[9px] text-emerald-600"><CheckCircle2 className="w-3 h-3" />Active</span>
                        : <span className="flex items-center gap-1 text-[9px] text-muted-foreground"><XCircle className="w-3 h-3" />{isExpired ? "Expired" : "Inactive"}</span>
                      }
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        {/* Activity */}
                        <button
                          onClick={() => loadActivity(l)}
                          data-testid={`activity-${l.id}`}
                          className="p-1 text-muted-foreground hover:text-primary transition-colors" title="View Activity">
                          <Activity className="w-3.5 h-3.5" />
                        </button>
                        {/* Share button + dropdown */}
                        {l.is_active && !isExpired && (
                          <div className="relative">
                            <button
                              onClick={() => setShareLink(shareLink?.id === l.id ? null : l)}
                              data-testid={`share-${l.id}`}
                              className="flex items-center gap-1 text-[10px] px-2 py-1 border border-border rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all whitespace-nowrap">
                              <Share2 className="w-3 h-3" />Share
                            </button>
                            {shareLink?.id === l.id && (
                              <ShareDropdown
                                link={l}
                                onWA={shareWhatsApp}
                                onEmail={shareEmail}
                                onClose={() => setShareLink(null)}
                              />
                            )}
                          </div>
                        )}
                        {/* Deactivate */}
                        {l.is_active && (
                          <button onClick={() => handleDeactivate(l.id)} data-testid={`deactivate-${l.id}`}
                            className="p-1 text-muted-foreground hover:text-red-500 transition-colors" title="Deactivate">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && links.length === 0 && (
                <tr><td colSpan={11} className="py-12 text-center text-muted-foreground text-sm">
                  No referral links yet. Click "Generate Link" to create the first one.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl" data-testid="create-link-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Generate Referral Link</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">✕</button>
            </div>
          <div className="px-6 py-5 space-y-4">
              {/* Link type selector — first choice */}
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Referral Type</label>
                <div className="flex gap-2">
                  {[
                    { v: "prospect", label: "Refer a Prospect", sub: "Subscription page" },
                    { v: "partner",  label: "Refer a Partner",  sub: "Become partner page" },
                  ].map(({ v, label, sub }) => (
                    <button key={v} onClick={() => setForm({ ...form, link_type: v, plan_id: "", billing_cycle: "monthly" })}
                      data-testid={`link-type-${v}`}
                      className={`flex-1 py-2.5 px-3 rounded-xl border text-left transition-all ${
                        form.link_type === v
                          ? "bg-primary/10 border-primary/40 text-primary"
                          : "border-border text-muted-foreground hover:bg-muted"
                      }`}>
                      <p className="text-[12px] font-semibold">{label}</p>
                      <p className="text-[10px] opacity-70 mt-0.5">{sub}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Partner picker */}
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Partner</label>
                <select value={form.partner_id} onChange={e => setForm({...form, partner_id: e.target.value})} className={inp} data-testid="select-partner">
                  <option value="">Select partner…</option>
                  {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              {/* Plan + Cycle — only for prospect */}
              {form.link_type === "prospect" && (
                <>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Plan</label>
                    <select value={form.plan_id} onChange={e => setForm({...form, plan_id: e.target.value})} className={inp} data-testid="select-plan">
                      <option value="">Select plan…</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Billing Cycle</label>
                    <div className="flex gap-2">
                      {["monthly", "yearly"].map(c => (
                        <button key={c} onClick={() => setForm({...form, billing_cycle: c})}
                          data-testid={`cycle-${c}`}
                          className={`flex-1 py-2 rounded-lg border text-[12px] font-medium capitalize transition-all ${
                            form.billing_cycle === c ? "bg-primary/10 border-primary/30 text-primary" : "border-border text-muted-foreground hover:bg-muted"
                          }`}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Landing Mode</label>
                  <select value={form.landing_mode} onChange={e => setForm({...form, landing_mode: e.target.value})} className={inp}>
                    <option value="">Default ({MODE_LABEL[config.default_landing_mode]})</option>
                    <option value="preview">Preview Page</option>
                    <option value="direct">Direct</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Expires After</label>
                  <select value={form.expiry_days} onChange={e => setForm({...form, expiry_days: e.target.value})} className={inp}>
                    <option value="">Default ({config.default_expiry_days} days)</option>
                    {EXPIRY_OPTS.map(d => <option key={d} value={d}>{d} days</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setShowCreate(false)}
                className="flex-1 py-2.5 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted transition-all">
                Cancel
              </button>
              <button onClick={handleCreate} disabled={saving} data-testid="confirm-create-link"
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all">
                {saving ? "Generating…" : "Generate Link"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm shadow-2xl" data-testid="config-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Global Link Config</h2>
              <button onClick={() => setShowConfig(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">✕</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Default Expiry</label>
                <div className="flex gap-2">
                  {EXPIRY_OPTS.map(d => (
                    <button key={d} onClick={() => setCfgForm({...cfgForm, default_expiry_days: d})}
                      className={`flex-1 py-2 rounded-lg border text-[12px] font-medium transition-all ${
                        cfgForm.default_expiry_days === d ? "bg-primary/10 border-primary/30 text-primary" : "border-border text-muted-foreground hover:bg-muted"
                      }`}>
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Default Landing Mode</label>
                <div className="flex gap-2">
                  {[["preview","Preview Page"],["direct","Direct Reg."]].map(([v, lbl]) => (
                    <button key={v} onClick={() => setCfgForm({...cfgForm, default_landing_mode: v})}
                      className={`flex-1 py-2 rounded-lg border text-[12px] font-medium transition-all ${
                        cfgForm.default_landing_mode === v ? "bg-primary/10 border-primary/30 text-primary" : "border-border text-muted-foreground hover:bg-muted"
                      }`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setShowConfig(false)}
                className="flex-1 py-2.5 border border-border rounded-xl text-[13px] text-muted-foreground hover:bg-muted transition-all">
                Cancel
              </button>
              <button onClick={saveConfig} disabled={saving}
                className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-xl text-[13px] font-medium hover:opacity-90 disabled:opacity-50 transition-all">
                {saving ? "Saving…" : "Save Config"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activity modal */}
      {activityLink && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary" />Link Activity
                </h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  <code className="text-primary font-mono">{activityLink.short_code}</code>
                  {activityLink.partner_name && ` · ${activityLink.partner_name}`}
                  {activityLink.link_type !== "partner" && activityLink.plan_name && ` · ${activityLink.plan_name}`}
                </p>
              </div>
              <button onClick={() => setActivityLink(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground text-lg leading-none">✕</button>
            </div>
            <div className="flex gap-5 px-6 py-3 bg-muted/20 border-b border-border text-[11px]">
              <span className="flex items-center gap-1.5"><MousePointerClick className="w-3 h-3 text-blue-500" /><span className="text-muted-foreground">Clicks:</span><span className="font-semibold">{activityLink.clicks || 0}</span></span>
              <span className="flex items-center gap-1.5"><UserCheck className="w-3 h-3 text-amber-500" /><span className="text-muted-foreground">Registered:</span><span className="font-semibold">{activityLink.registrations || 0}</span></span>
              <span className="flex items-center gap-1.5"><Zap className="w-3 h-3 text-emerald-500" /><span className="text-muted-foreground">Activated:</span><span className="font-semibold">{activityLink.activations || 0}</span></span>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {activityLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : activityData.length === 0 ? (
                <div className="text-center py-10">
                  <Activity className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-30" />
                  <p className="text-sm text-muted-foreground">No activity recorded yet</p>
                  <p className="text-[11px] text-muted-foreground/70 mt-1">Share this link to start tracking engagement</p>
                </div>
              ) : (
                activityData.map((ev, i) => <ActivityRow key={ev.id || i} event={ev} />)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Share dropdown — appears below the Share button ────────────────────────
function ShareDropdown({ link, onWA, onEmail, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (!e.target.closest("[data-share-dropdown]")) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div data-share-dropdown data-testid="share-dropdown"
      className="absolute right-0 top-full mt-1.5 z-30 bg-card border border-border rounded-xl shadow-xl overflow-hidden min-w-[152px]">
      <button onClick={() => onWA(link)} data-testid="share-whatsapp"
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-[12px] text-foreground hover:bg-muted transition-colors text-left">
        <MessageCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
        WhatsApp
      </button>
      <button onClick={() => onEmail(link)} data-testid="share-email"
        className="flex items-center gap-2.5 w-full px-4 py-2.5 text-[12px] text-foreground hover:bg-muted transition-colors text-left border-t border-border">
        <Mail className="w-3.5 h-3.5 text-blue-500 shrink-0" />
        Email
      </button>
    </div>
  );
}

// ── Activity event row ────────────────────────────────────────────────────────
function ActivityRow({ event }) {
  const cfg = {
    click:        { label: "Click",      color: "bg-blue-500/10 text-blue-600 border-blue-500/20",          dot: "bg-blue-500" },
    registration: { label: "Registered", color: "bg-amber-500/10 text-amber-600 border-amber-500/20",       dot: "bg-amber-500" },
    activation:   { label: "Activated",  color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20", dot: "bg-emerald-500" },
  }[event.event_type] || { label: event.event_type, color: "bg-muted text-muted-foreground", dot: "bg-muted" };
  const time = event.created_at
    ? fmtDateTime(event.created_at)
    : "—";
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-border/50 last:border-0">
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${cfg.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${cfg.color}`}>{cfg.label}</span>
          <span className="text-[11px] text-muted-foreground">{time}</span>
        </div>
        {event.event_type === "click" && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{event.browser || "Browser"} · IP: {event.ip_masked || "—"}</p>
        )}
        {(event.event_type === "registration" || event.event_type === "activation") && (
          <p className="text-[12px] mt-0.5">
            {event.name && <span className="font-medium text-foreground">{event.name}</span>}
            {event.email && <span className="text-muted-foreground"> · {event.email}</span>}
          </p>
        )}
      </div>
    </div>
  );
}
